import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { styled, keyframes } from '@mui/system';

const events = [
  { status: 'Requested', date: '15/10/2020 10:30', icon: 'pending', color: '#9C27B0' },
  { status: 'Approved', date: '15/10/2020 14:00', icon: 'add_task', color: '#673AB7' },
  { status: 'Scheduled', date: '15/10/2020 16:15', icon: 'local_shipping', color: '#FF9800' },
  { status: 'Completed', date: '16/10/2020 10:00', icon: 'done_all', color: '#2E7D32' }
];

// Pulse animation for current step
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0,0,0,0.2); }
  70% { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
`;

const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: theme.spacing(3, 2),
  overflowX: 'auto', // horizontal scroll for small screens
}));

const ProgressLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 'calc(50% - 14px)', // adjust so it passes through marker center
  left: 0,
  right: 0,
  height: 4,
  backgroundColor: '#e0e0e0',
  borderRadius: 2,
  zIndex: 0,
}));

const FilledLine = styled(Box)(({ progress }) => ({
  position: 'absolute',
  top: 'calc(50% - 14px)', // same as ProgressLine
  left: 0,
  height: 4,
  background: 'linear-gradient(to right, #9C27B0, #673AB7, #FF9800, #607D8B)',
  borderRadius: 2,
  width: `${progress}%`,
  transition: 'width 0.4s ease',
  zIndex: 0,
}));

const Marker = styled(Box)(({ color, isCurrent }) => ({
  width: isCurrent ? 44 : 36,
  height: isCurrent ? 44 : 36,
  borderRadius: '50%',
  backgroundColor: color,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  transform: isCurrent ? 'scale(1.2)' : 'scale(1)',
  transition: 'all 0.3s ease',
  zIndex: 2, // above the line
  animation: isCurrent ? `${pulse} 1.5s infinite` : 'none',
}));

const Timeline = ({ status, dispatch }) => {
  const steps = {
    requested: 1,
    approved: 2,
    scheduled: 3,
    completed: 4,
  };

  const formatDate = (date) => {
    if (!date) return undefined;
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toLocaleDateString();
  };

  const tooltipDates = {
    requested: formatDate(dispatch?.req_date),
    approved: formatDate(dispatch?.estimated_delivery_date),
    scheduled: formatDate(dispatch?.actual_delivery_date),
    completed: formatDate(dispatch?.completion_date || dispatch?.actual_delivery_date),
  };

  const tooltipMessages = {
    requested: (date, reached) =>
      reached
        ? `Dispatch requested${date ? ` on ${date}` : ''}.`
        : 'Dispatch request pending.',
    approved: (date, reached) =>
      reached
        ? `Dispatch approved. Estimated delivery date${date ? `: ${date}` : ' pending'}.`
        : 'Awaiting approval.',
    scheduled: (date, reached) =>
      reached
        ? `Dispatch scheduled${date ? ` for ${date}` : ''}.`
        : 'Awaiting scheduling.',
    completed: (date, reached) =>
      reached
        ? `Dispatch completed${date ? ` on ${date}` : ''}.`
        : 'Awaiting completion.',
  };

  const getTooltipTitle = (stepKey, hasReached) => {
    const date = tooltipDates[stepKey];
    return tooltipMessages[stepKey](date, hasReached);
  };

  const currentStep = steps[String(status || '').toLowerCase()] || 1; // Default to step 1 if status not found
  const progress = ((currentStep - 1) / (events.length - 1)) * 100;

  return (
    <TimelineContainer>
      <ProgressLine />
      <FilledLine progress={progress} />
      {events.map((item, index) => {
        const stepKey = item.status.toLowerCase();
        const hasReached = index + 1 <= currentStep;
        return (
          <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
            <Tooltip title={getTooltipTitle(stepKey, hasReached)}>
              <Marker color={item.color} isCurrent={index + 1 === currentStep}>
                <span className="material-icons">{item.icon}</span>
              </Marker>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{ mt: 1, display: 'block', fontWeight: index + 1 === currentStep ? 'bold' : 'normal' }}
            >
              {item.status}
            </Typography>
          </Box>
        )
      })}
    </TimelineContainer>
  );
};

export default Timeline;
